import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { X, Save, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Modal = ({ isOpen, onClose, title, children, wide }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className={`bg-[#111111] rounded-2xl border border-white/10 ${wide ? 'w-full max-w-2xl' : 'w-full max-w-md'} max-h-[85vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <h3 className="font-['Outfit'] font-medium text-lg">{title}</h3>
          <button onClick={onClose} className="text-[#A1A1AA] hover:text-white" data-testid="modal-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <label className="text-xs text-[#A1A1AA] uppercase tracking-wider">{label}</label>
    {children}
  </div>
);

const inputCls = "w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-[#555] focus:outline-none focus:border-[#0055FF] transition-colors";
const selectCls = "w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#0055FF] transition-colors appearance-none";

// ==================== EDIT CLIENT ====================
export const EditClientModal = ({ isOpen, onClose, client, onSave, token }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (client) setForm({
      email: client.email || '',
      company_name: client.company_name || '',
      role: client.role || 'user',
      is_active: client.is_active !== false
    });
  }, [client]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await axios.put(`${API_URL}/api/admin/clients/${client.id}`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onSave();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Modifica Cliente">
      <div className="space-y-4">
        <Field label="Email">
          <input className={inputCls} value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="edit-client-email" />
        </Field>
        <Field label="Azienda">
          <input className={inputCls} value={form.company_name || ''} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="edit-client-company" />
        </Field>
        <Field label="Ruolo">
          <select className={selectCls} value={form.role || 'user'} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="edit-client-role">
            <option value="user">User</option>
            <option value="admin">Admin</option>
            <option value="sub_account">Sub-account</option>
          </select>
        </Field>
        <Field label="Stato">
          <select className={selectCls} value={form.is_active === false ? 'inactive' : 'active'} onChange={(e) => setForm({ ...form, is_active: e.target.value === 'active' })} data-testid="edit-client-status">
            <option value="active">Attivo</option>
            <option value="inactive">Disattivato</option>
          </select>
        </Field>
        {error && <p className="text-xs text-[#EF4444]">{error}</p>}
        <div className="flex gap-3 pt-2">
          <Button onClick={onClose} variant="outline" className="flex-1 border-white/10 text-[#A1A1AA] hover:text-white">Annulla</Button>
          <Button onClick={handleSave} disabled={saving} className="flex-1 bg-[#0055FF] hover:bg-[#3377FF]" data-testid="save-client-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Salva</>}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// ==================== EDIT LICENSE ====================
export const EditLicenseModal = ({ isOpen, onClose, license, onSave, token }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    if (license) setForm({
      status: license.status || 'active',
      api_calls_limit: license.api_calls_limit || 0,
      api_calls_used: license.api_calls_used || 0,
      expires_at: license.expires_at ? license.expires_at.substring(0, 10) : '',
      package_name: license.package_name || '',
      domain: license.domain || ''
    });
  }, [license]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await axios.put(`${API_URL}/api/admin/licenses/${license.id}`, {
        ...form,
        api_calls_limit: parseInt(form.api_calls_limit) || 0,
        api_calls_used: parseInt(form.api_calls_used) || 0,
        expires_at: form.expires_at || ''
      }, { headers: { Authorization: `Bearer ${token}` } });
      onSave();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleResetDomain = async () => {
    if (!window.confirm(`Resettare il dominio "${form.domain || 'N/A'}" per questa licenza? Il prossimo sito che attiva la licenza diventerà il nuovo dominio.`)) return;
    setResetting(true);
    setError('');
    try {
      await axios.post(`${API_URL}/api/admin/licenses/${license.id}/reset-domain`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setForm({ ...form, domain: '' });
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore nel reset dominio');
    } finally {
      setResetting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Modifica Licenza">
      <div className="space-y-4">
        <div className="p-3 bg-white/5 rounded-lg">
          <span className="text-[10px] text-[#A1A1AA]">License Key</span>
          <p className="text-sm font-mono text-[#00E5FF]">{license?.license_key}</p>
        </div>
        <Field label="Dominio Attivo">
          <div className="flex gap-2">
            <input className={inputCls + " flex-1"} value={form.domain || ''} onChange={(e) => setForm({ ...form, domain: e.target.value })} placeholder="Nessun dominio registrato" data-testid="edit-license-domain" />
            <button
              type="button"
              onClick={handleResetDomain}
              disabled={resetting || !form.domain}
              className="px-3 py-1.5 text-xs rounded-lg bg-[#EF4444]/20 text-[#EF4444] hover:bg-[#EF4444]/30 disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
              data-testid="reset-domain-btn"
            >
              {resetting ? 'Reset...' : 'Reset Dominio'}
            </button>
          </div>
        </Field>
        <Field label="Package">
          <input className={inputCls} value={form.package_name || ''} onChange={(e) => setForm({ ...form, package_name: e.target.value })} data-testid="edit-license-package" />
        </Field>
        <Field label="Status">
          <select className={selectCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="edit-license-status">
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="expired">Expired</option>
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Limite Crediti">
            <input type="number" className={inputCls} value={form.api_calls_limit || ''} onChange={(e) => setForm({ ...form, api_calls_limit: e.target.value })} data-testid="edit-license-limit" />
          </Field>
          <Field label="Crediti Usati">
            <input type="number" className={inputCls} value={form.api_calls_used || ''} onChange={(e) => setForm({ ...form, api_calls_used: e.target.value })} data-testid="edit-license-used" />
          </Field>
        </div>
        <Field label="Scadenza">
          <input type="date" className={inputCls} value={form.expires_at || ''} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} data-testid="edit-license-expiry" />
        </Field>
        {error && <p className="text-xs text-[#EF4444]">{error}</p>}
        <div className="flex gap-3 pt-2">
          <Button onClick={onClose} variant="outline" className="flex-1 border-white/10 text-[#A1A1AA] hover:text-white">Annulla</Button>
          <Button onClick={handleSave} disabled={saving} className="flex-1 bg-[#0055FF] hover:bg-[#3377FF]" data-testid="save-license-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Salva</>}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// ==================== CREATE LICENSE ====================
export const CreateLicenseModal = ({ isOpen, onClose, packages, onSave, token }) => {
  const [form, setForm] = useState({ user_email: '', package_id: '', api_calls_limit: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        user_email: form.user_email,
        package_id: form.package_id,
      };
      if (form.api_calls_limit) payload.api_calls_limit = parseInt(form.api_calls_limit);
      await axios.post(`${API_URL}/api/admin/licenses`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onSave();
      onClose();
      setForm({ user_email: '', package_id: '', api_calls_limit: '' });
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Crea Licenza Manuale">
      <div className="space-y-4">
        <Field label="Email Utente">
          <input className={inputCls} placeholder="utente@email.com" value={form.user_email} onChange={(e) => setForm({ ...form, user_email: e.target.value })} data-testid="create-license-email" />
        </Field>
        <Field label="Pacchetto">
          <select className={selectCls} value={form.package_id} onChange={(e) => setForm({ ...form, package_id: e.target.value })} data-testid="create-license-package">
            <option value="">Seleziona pacchetto...</option>
            {Object.entries(packages || {}).map(([id, pkg]) => (
              <option key={id} value={id}>{pkg.name} - {pkg.price}EUR</option>
            ))}
          </select>
        </Field>
        <Field label="Crediti personalizzati (opzionale)">
          <input type="number" className={inputCls} placeholder="Lascia vuoto per default" value={form.api_calls_limit} onChange={(e) => setForm({ ...form, api_calls_limit: e.target.value })} data-testid="create-license-credits" />
        </Field>
        {error && <p className="text-xs text-[#EF4444]">{error}</p>}
        <div className="flex gap-3 pt-2">
          <Button onClick={onClose} variant="outline" className="flex-1 border-white/10 text-[#A1A1AA] hover:text-white">Annulla</Button>
          <Button onClick={handleSave} disabled={saving || !form.user_email || !form.package_id} className="flex-1 bg-[#10B981] hover:bg-[#059669]" data-testid="create-license-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Crea Licenza'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// ==================== EDIT PACKAGE ====================
export const EditPackageModal = ({ isOpen, onClose, pkg, packageId, onSave, token }) => {
  const [form, setForm] = useState({});
  const [featuresText, setFeaturesText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (pkg) {
      setForm({
        name: pkg.name || '',
        price: pkg.price || 0,
        api_calls_limit: pkg.api_calls_limit || 0,
        initial_credits: pkg.initial_credits || 0,
        monthly_credits: pkg.monthly_credits || 0,
      });
      setFeaturesText((pkg.features || []).join('\n'));
    }
  }, [pkg]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        name: form.name,
        price: parseFloat(form.price),
        api_calls_limit: parseInt(form.api_calls_limit) || 0,
        features: featuresText.split('\n').filter(f => f.trim())
      };
      if (form.initial_credits) payload.initial_credits = parseInt(form.initial_credits);
      if (form.monthly_credits) payload.monthly_credits = parseInt(form.monthly_credits);
      await axios.put(`${API_URL}/api/admin/packages/${packageId}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onSave();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Modifica Piano: ${pkg?.name || ''}`}>
      <div className="space-y-4">
        <Field label="Nome">
          <input className={inputCls} value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-pkg-name" />
        </Field>
        <Field label="Prezzo (EUR)">
          <input type="number" step="0.01" className={inputCls} value={form.price || ''} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="edit-pkg-price" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Crediti (limite)">
            <input type="number" className={inputCls} value={form.api_calls_limit || ''} onChange={(e) => setForm({ ...form, api_calls_limit: e.target.value })} data-testid="edit-pkg-limit" />
          </Field>
          <Field label={pkg?.type === 'subscription' ? 'Crediti Mensili' : 'Crediti Iniziali'}>
            <input type="number" className={inputCls} value={pkg?.type === 'subscription' ? (form.monthly_credits || '') : (form.initial_credits || '')} onChange={(e) => setForm({ ...form, [pkg?.type === 'subscription' ? 'monthly_credits' : 'initial_credits']: e.target.value })} data-testid="edit-pkg-credits" />
          </Field>
        </div>
        <Field label="Features (una per riga)">
          <textarea className={`${inputCls} min-h-[120px] resize-y`} value={featuresText} onChange={(e) => setFeaturesText(e.target.value)} data-testid="edit-pkg-features" />
        </Field>
        {error && <p className="text-xs text-[#EF4444]">{error}</p>}
        <div className="flex gap-3 pt-2">
          <Button onClick={onClose} variant="outline" className="flex-1 border-white/10 text-[#A1A1AA] hover:text-white">Annulla</Button>
          <Button onClick={handleSave} disabled={saving} className="flex-1 bg-[#0055FF] hover:bg-[#3377FF]" data-testid="save-pkg-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Salva</>}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// ==================== EDIT TOPUP PACKAGE ====================
export const EditTopupModal = ({ isOpen, onClose, pkg, packageId, onSave, token }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (pkg) setForm({
      name: pkg.name || '',
      credits: pkg.credits || 0,
      price: pkg.price || 0,
    });
  }, [pkg]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await axios.put(`${API_URL}/api/admin/topup-packages/${packageId}`, {
        name: form.name,
        credits: parseInt(form.credits),
        price: parseFloat(form.price),
      }, { headers: { Authorization: `Bearer ${token}` } });
      onSave();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Modifica Top-up: ${pkg?.name || ''}`}>
      <div className="space-y-4">
        <Field label="Nome">
          <input className={inputCls} value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-topup-name" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Crediti">
            <input type="number" className={inputCls} value={form.credits || ''} onChange={(e) => setForm({ ...form, credits: e.target.value })} data-testid="edit-topup-credits" />
          </Field>
          <Field label="Prezzo (EUR)">
            <input type="number" step="0.01" className={inputCls} value={form.price || ''} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="edit-topup-price" />
          </Field>
        </div>
        {error && <p className="text-xs text-[#EF4444]">{error}</p>}
        <div className="flex gap-3 pt-2">
          <Button onClick={onClose} variant="outline" className="flex-1 border-white/10 text-[#A1A1AA] hover:text-white">Annulla</Button>
          <Button onClick={handleSave} disabled={saving} className="flex-1 bg-[#0055FF] hover:bg-[#3377FF]" data-testid="save-topup-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Salva</>}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// ==================== EDIT TRANSACTION ====================
export const EditTransactionModal = ({ isOpen, onClose, transaction, onSave, token }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (transaction) setForm({
      payment_status: transaction.payment_status || 'initiated',
      notes: transaction.admin_notes || '',
    });
  }, [transaction]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const txId = transaction.id || transaction.session_id;
      await axios.put(`${API_URL}/api/admin/transactions/${txId}`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onSave();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Modifica Transazione">
      <div className="space-y-4">
        <div className="p-3 bg-white/5 rounded-lg space-y-1">
          <div className="flex justify-between text-xs"><span className="text-[#A1A1AA]">Email</span><span>{transaction?.email || 'N/A'}</span></div>
          <div className="flex justify-between text-xs"><span className="text-[#A1A1AA]">Package</span><span>{transaction?.package_id}</span></div>
          <div className="flex justify-between text-xs"><span className="text-[#A1A1AA]">Importo</span><span>{transaction?.amount}EUR</span></div>
        </div>
        <Field label="Stato Pagamento">
          <select className={selectCls} value={form.payment_status} onChange={(e) => setForm({ ...form, payment_status: e.target.value })} data-testid="edit-tx-status">
            <option value="initiated">Initiated</option>
            <option value="pending">Pending</option>
            <option value="paid">Paid</option>
            <option value="failed">Failed</option>
            <option value="refunded">Refunded</option>
          </select>
        </Field>
        <Field label="Note Admin">
          <textarea className={`${inputCls} min-h-[80px] resize-y`} placeholder="Note opzionali..." value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="edit-tx-notes" />
        </Field>
        {error && <p className="text-xs text-[#EF4444]">{error}</p>}
        <div className="flex gap-3 pt-2">
          <Button onClick={onClose} variant="outline" className="flex-1 border-white/10 text-[#A1A1AA] hover:text-white">Annulla</Button>
          <Button onClick={handleSave} disabled={saving} className="flex-1 bg-[#0055FF] hover:bg-[#3377FF]" data-testid="save-tx-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Salva</>}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// ==================== EDIT CREDIT COSTS ====================
export const EditCreditCostsModal = ({ isOpen, onClose, costs, onSave, token }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (costs) setForm({ ...costs });
  }, [costs]);

  const labels = {
    seo_analysis: 'Analisi SEO',
    meta_generation: 'Meta Tag',
    content_generation_short: 'Contenuto Corto',
    content_generation_medium: 'Contenuto Medio',
    content_generation_long: 'Contenuto Lungo',
    local_seo: 'Local SEO',
    aeo_analysis: 'Analisi AEO',
    aeo_content: 'Contenuto AEO',
    demo_analysis: 'Demo (gratis)',
    image_generation: 'Generazione Immagine AI',
    complete_article: 'Articolo Completo AI',
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const parsed = {};
      for (const [k, v] of Object.entries(form)) {
        parsed[k] = parseInt(v) || 0;
      }
      await axios.put(`${API_URL}/api/admin/credit-costs`, { costs: parsed }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onSave();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Modifica Costi Crediti">
      <div className="space-y-3">
        <p className="text-xs text-[#A1A1AA] mb-2">Crediti consumati per ogni operazione AI:</p>
        {Object.entries(form).map(([key, value]) => (
          <div key={key} className="flex items-center justify-between gap-3">
            <span className="text-sm text-[#A1A1AA] flex-1">{labels[key] || key}</span>
            <input
              type="number"
              min="0"
              className={`${inputCls} w-24 text-center`}
              value={value}
              onChange={(e) => setForm({ ...form, [key]: e.target.value })}
              data-testid={`edit-cost-${key}`}
            />
            <span className="text-[10px] text-[#A1A1AA] w-8">cr</span>
          </div>
        ))}
        {error && <p className="text-xs text-[#EF4444]">{error}</p>}
        <div className="flex gap-3 pt-3">
          <Button onClick={onClose} variant="outline" className="flex-1 border-white/10 text-[#A1A1AA] hover:text-white">Annulla</Button>
          <Button onClick={handleSave} disabled={saving} className="flex-1 bg-[#0055FF] hover:bg-[#3377FF]" data-testid="save-costs-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Salva</>}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
